import { create } from 'zustand';

export type ThemeMode = 'dark' | 'light';
export type SidebarTab = 'thumbnails' | 'bookmarks' | 'attachments' | 'annotations';

interface UIState {
  theme: ThemeMode;
  sidebarVisible: boolean;
  sidebarWidth: number;
  sidebarTab: SidebarTab;
  propertiesPanelVisible: boolean;
  searchPanelVisible: boolean;
  isFullscreen: boolean;

  /* Dialogs */
  mergeDialogOpen: boolean;
  splitDialogOpen: boolean;
  convertDialogOpen: boolean;
  compressDialogOpen: boolean;
  pageOrganizerOpen: boolean;
  ocrDialogOpen: boolean;
  signatureDialogOpen: boolean;
  propertiesDialogOpen: boolean;
  settingsDialogOpen: boolean;
  loginDialogOpen: boolean;
  adminDialogOpen: boolean;

  /* Toast */
  toasts: Toast[];

  /* Actions */
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  toggleSidebar: () => void;
  setSidebarTab: (tab: SidebarTab) => void;
  setSidebarWidth: (width: number) => void;
  toggleSearchPanel: () => void;
  toggleFullscreen: () => void;
  openDialog: (dialog: DialogName) => void;
  closeDialog: (dialog: DialogName) => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

type DialogName =
  | 'merge'
  | 'split'
  | 'convert'
  | 'compress'
  | 'pageOrganizer'
  | 'ocr'
  | 'signature'
  | 'properties'
  | 'settings'
  | 'login'
  | 'admin';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number;
}

const dialogKeyMap: Record<DialogName, keyof UIState> = {
  merge: 'mergeDialogOpen',
  split: 'splitDialogOpen',
  convert: 'convertDialogOpen',
  compress: 'compressDialogOpen',
  pageOrganizer: 'pageOrganizerOpen',
  ocr: 'ocrDialogOpen',
  signature: 'signatureDialogOpen',
  properties: 'propertiesDialogOpen',
  settings: 'settingsDialogOpen',
  login: 'loginDialogOpen',
  admin: 'adminDialogOpen',
};

export const useUIStore = create<UIState>((set, get) => ({
  theme: 'dark',
  sidebarVisible: true,
  sidebarWidth: 240,
  sidebarTab: 'thumbnails',
  propertiesPanelVisible: false,
  searchPanelVisible: false,
  isFullscreen: false,

  mergeDialogOpen: false,
  splitDialogOpen: false,
  convertDialogOpen: false,
  compressDialogOpen: false,
  pageOrganizerOpen: false,
  ocrDialogOpen: false,
  signatureDialogOpen: false,
  propertiesDialogOpen: false,
  settingsDialogOpen: false,
  loginDialogOpen: false,
  adminDialogOpen: false,

  toasts: [],

  setTheme: (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    set({ theme });
  },

  toggleTheme: () => {
    const newTheme = get().theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', newTheme);
    set({ theme: newTheme });
  },

  toggleSidebar: () => set((s) => ({ sidebarVisible: !s.sidebarVisible })),
  setSidebarTab: (tab) => set({ sidebarTab: tab, sidebarVisible: true }),
  setSidebarWidth: (width) => set({ sidebarWidth: Math.max(180, Math.min(500, width)) }),

  toggleSearchPanel: () => set((s) => ({ searchPanelVisible: !s.searchPanelVisible })),

  toggleFullscreen: () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
      set({ isFullscreen: true });
    } else {
      document.exitFullscreen?.().catch(() => {});
      set({ isFullscreen: false });
    }
  },

  openDialog: (dialog) => {
    const key = dialogKeyMap[dialog];
    if (key) set({ [key]: true } as Partial<UIState>);
  },

  closeDialog: (dialog) => {
    const key = dialogKeyMap[dialog];
    if (key) set({ [key]: false } as Partial<UIState>);
  },

  addToast: (toast) => {
    const id = crypto.randomUUID();
    const newToast = { ...toast, id };
    set((s) => ({ toasts: [...s.toasts, newToast] }));
    setTimeout(() => {
      get().removeToast(id);
    }, toast.duration || 4000);
  },

  removeToast: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));
